/*
 * Author: Dw
 * File Created: Tuesday, 25-06-10 11:25 am
 * Last Modified: Wednesday, 25-06-18 03:07 pm, by Ben Zhao
 */

import CryptoJS from 'crypto-js';
import { EventEmitter } from 'events';

import { sleep } from './promise';

export enum BasicTaskStatus {
  INIT,
  FETCHING,
  FETCHED,
  WORKING,
  DONE,
  ERROR,
}

interface BasicTaskItemConfig {
  fetchFn?: () => Promise<void>;
  onFinished?: () => void;
  onError?: () => void;
  onFetched: () => void | Promise<boolean | void>;
}

class Deferred {
  promise: Promise<void>;

  resolve = () => {};

  reject = () => {};

  constructor() {
    this.promise = new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }
}

class BasicTaskItem {
  /** 基础任务元的状态 */
  private _status: BasicTaskStatus = BasicTaskStatus.INIT;

  /** task id */
  private _id: string = '';

  /** [Optional] 保存外部传入的 fetch 函数 */
  private _fetchFn: BasicTaskItemConfig['fetchFn'] | undefined;

  /** [Optional] 保存外部传入的 onFetched 函数 */
  private _onFetched: BasicTaskItemConfig['onFetched'];

  /** [Optional] 保存外部传入的 onFinished 函数 */
  private _onFinished: (_ignoreDelay?: boolean) => void | undefined;

  /** [Optional] 保存外部传入的 onError 函数 */
  private _onError: BasicTaskItemConfig['onError'] | undefined;

  private _fetchPromise: Deferred | undefined;

  constructor(
    config: Omit<BasicTaskItemConfig, 'onFinished'> & {
      onFinished: (ignoreDelay?: boolean) => void;
    }
  ) {
    const { fetchFn } = config || {};

    this._id = CryptoJS.lib.WordArray.random(16).toString(CryptoJS.enc.Hex);
    this._status = BasicTaskStatus.INIT;
    this._fetchFn = fetchFn;
    this._onFinished = config?.onFinished;
    this._onError = config?.onError;
    this._onFetched = config.onFetched;
  }

  async startWorking() {
    if (this._status !== BasicTaskStatus.FETCHED) {
      return;
    }
    this._status = BasicTaskStatus.WORKING;
    try {
      const ignoreDelay = await this._onFetched();
      this._status = BasicTaskStatus.DONE;

      this._onFinished?.(ignoreDelay ?? false);
      return true;
    } catch (error) {
      this.enhancedOnError();
      console.error('BasicTaskItem working status failed: ', error);
      return false;
    }
  }

  private enhancedOnError() {
    this._status = BasicTaskStatus.ERROR;
    this._onError?.();
  }

  async execute() {
    if (this._status === BasicTaskStatus.INIT) {
      const fetchFn = this._fetchFn;
      this._status = BasicTaskStatus.FETCHING;

      if (fetchFn) {
        try {
          await fetchFn();
        } catch (error) {
          this.enhancedOnError();
          console.error('BasicTaskItem start failed: ', error);
        } finally {
          this._status = BasicTaskStatus.FETCHED;
        }
      } else {
        this._fetchPromise = new Deferred();
        await this._fetchPromise.promise;
        this._status = BasicTaskStatus.FETCHED;
      }
    } else if (this._status === BasicTaskStatus.FETCHED) {
      await this.startWorking();
    }
  }

  finishFetch() {
    if (this._fetchFn || !this._fetchPromise) {
      return;
    }
    this._fetchPromise.resolve();
  }

  getId() {
    return this._id;
  }

  getStatus() {
    return this._status;
  }
}

export class TaskQueue {
  private _delay: number;

  private _taskQueue: { task: BasicTaskItem; priority: number }[] = [];

  private _activeIdx: number = -1;

  private _isRunning = false;

  private _addInQueue(taskInfo: (typeof this._taskQueue)[number]) {
    const { priority } = taskInfo;

    // 优先级高的排在前面
    const taskIndex = this._taskQueue.findIndex(
      (item) => item.priority < priority
    );

    if (taskIndex === -1) {
      this._taskQueue.push(taskInfo);
    } else {
      this._taskQueue.splice(taskIndex, 0, taskInfo);
    }
  }

  constructor(delay = 1000) {
    this._delay = delay;
  }

  private _enhanceTaskConfig(config: BasicTaskItemConfig): Omit<
    BasicTaskItemConfig,
    'onFinished'
  > & {
    onFinished: (ignoreDelay?: boolean) => void;
  } {
    const { fetchFn, onFinished, onFetched, onError } = config;

    return {
      fetchFn,
      onFetched,
      onError: () => {
        onError?.();
        this._processNextTask(true);
      },
      onFinished: (ignoreDelay) => {
        onFinished?.();
        this._processNextTask(ignoreDelay);
      },
    };
  }

  private async _processNextTask(ignoreDelay?: boolean) {
    if (this._activeIdx >= 0 && this._activeIdx < this._taskQueue.length - 1) {
      this._activeIdx++;
      if (!ignoreDelay) {
        await sleep(this._delay);
      }
      await this._executeCurrentTask();
    } else if (this._activeIdx === this._taskQueue.length - 1) {
      // 所有任务已完成
      this._isRunning = false;
      this._activeIdx = -1;
    }
  }

  private async _executeCurrentTask() {
    if (this._activeIdx < 0) {
      return;
    }
    if (this._activeIdx >= this._taskQueue.length) {
      return;
    }

    const currentTask = this._taskQueue[this._activeIdx]?.task;

    await currentTask?.execute();
  }

  push(taskInfo: {
    task: BasicTaskItemConfig;
    priority: number;
  }): BasicTaskItem | undefined {
    if (this._isRunning) {
      return;
    }
    const newItem = new BasicTaskItem(this._enhanceTaskConfig(taskInfo.task));

    this._addInQueue({
      task: newItem,
      priority: taskInfo.priority,
    });

    if (this._activeIdx === -1) {
      this._activeIdx = 0;
    }

    return newItem;
  }

  async execute(): Promise<void> {
    if (this._isRunning || this._taskQueue.length === 0) {
      return;
    }

    this._isRunning = true;

    // 先并行发起所有请求
    await Promise.all(
      this._taskQueue.map(async (item, idx) => {
        const status = item.task.getStatus();
        if (status === BasicTaskStatus.INIT) {
          await item.task.execute();
          if (this._activeIdx !== idx) {
            return;
          }
          await this._executeCurrentTask();
        }
      })
    );
  }
}
